/* =====================================================================
   VESSEL 2.0 – Interaktionen & Animationen
   Läuft als ES-Modul neben Horizon. Nutzt Horizons Cart-Events,
   damit Warenkorb-Drawer und Zähler automatisch aktualisiert werden.
   ===================================================================== */
import { CartAddEvent } from '@theme/events';

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const money = (cents) => {
  const fmt = (window.v2Settings && window.v2Settings.moneyFormat) || '{{amount_with_comma_separator}} €';
  const value = (cents / 100).toFixed(2);
  const [int, dec] = value.split('.');
  const withComma = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec;
  const withDot = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec;
  return fmt
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withComma.split(',')[0])
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withDot.split('.')[0])
    .replace(/\{\{\s*amount\s*\}\}/, withDot)
    .replace(/<[^>]+>/g, '');
};
const t = (key, fallback) => (window.v2Settings && window.v2Settings.strings && window.v2Settings.strings[key]) || fallback;

/* ---------- Header-Höhe als CSS-Variable ---------- */
function trackHeaderHeight() {
  const header = $('#header-group');
  if (!header) return;
  const set = () => document.documentElement.style.setProperty('--v2-header-h', `${Math.round(header.getBoundingClientRect().height)}px`);
  set();
  new ResizeObserver(set).observe(header);
}

/* ---------- Text zeilenweise aufteilen (split-lines) ---------- */
function splitLines(el) {
  if (el.dataset.v2SplitDone) return;
  const text = el.textContent.trim().replace(/\s+/g, ' ');
  if (!text) return;
  el.setAttribute('aria-label', text);
  el.textContent = '';
  const words = text.split(' ').map((w) => {
    const span = document.createElement('span');
    span.textContent = w + ' ';
    span.style.display = 'inline-block';
    span.style.whiteSpace = 'pre';
    el.appendChild(span);
    return span;
  });
  const lines = [];
  let top = null;
  words.forEach((w) => {
    if (top === null || Math.abs(w.offsetTop - top) > 4) { lines.push([]); top = w.offsetTop; }
    lines[lines.length - 1].push(w.textContent);
  });
  el.textContent = '';
  el.classList.add('v2-split');
  lines.forEach((words, i) => {
    const line = document.createElement('span');
    line.className = 'v2-split__line';
    line.setAttribute('aria-hidden', 'true');
    const inner = document.createElement('span');
    inner.className = 'v2-split__inner';
    inner.style.setProperty('--line-index', i);
    inner.textContent = words.join('').trimEnd();
    line.appendChild(inner);
    el.appendChild(line);
  });
  el.dataset.v2SplitDone = '1';
}

/* ---------- Reveal beim Scrollen ---------- */
let revealObserver;
function initReveal(root = document) {
  $$('[data-v2-split]', root).forEach(splitLines);
  const targets = $$('[data-v2-reveal], .v2-split, [data-v2-zoom]', root).filter((el) => !el.classList.contains('is-revealed'));
  if (reduceMotion || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  if (!revealObserver) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  }
  targets.forEach((el) => revealObserver.observe(el));
}

/* ---------- Akkordeons mit weicher Höhenanimation ---------- */
function initAccordions(root = document) {
  $$('.v2-acc details', root).forEach((details) => {
    if (details.dataset.v2Bound) return;
    details.dataset.v2Bound = '1';
    const summary = $('summary', details);
    const content = $('.v2-acc__content', details);
    if (!summary || !content) return;
    summary.addEventListener('click', (e) => {
      if (reduceMotion) return;
      e.preventDefault();
      const opening = !details.open;
      if (opening) {
        details.open = true;
        const h = content.scrollHeight;
        content.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 420, easing: 'cubic-bezier(0.215,0.61,0.355,1)' });
      } else {
        const h = content.scrollHeight;
        const anim = content.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 320, easing: 'cubic-bezier(0.645,0.045,0.355,1)' });
        anim.onfinish = () => { details.open = false; };
      }
    });
  });
}

/* ---------- Slider-Pfeile ---------- */
function initSliders(root = document) {
  $$('[data-v2-slider]', root).forEach((slider) => {
    if (slider.dataset.v2Bound) return;
    slider.dataset.v2Bound = '1';
    const track = $('[data-v2-track]', slider);
    const prev = $('[data-v2-prev]', slider);
    const next = $('[data-v2-next]', slider);
    const bar = $('[data-v2-progress]', slider);
    if (!track) return;
    const step = () => {
      const item = track.firstElementChild;
      return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      if (bar) {
        const ratio = track.clientWidth / track.scrollWidth;
        const pos = max > 0 ? track.scrollLeft / max : 0;
        bar.style.width = `${Math.min(100, ratio * 100)}%`;
        bar.style.transform = `translateX(${pos * ((1 / Math.max(ratio, 0.01)) - 1) * 100}%)`;
      }
      const dots = $$('[data-v2-dot]', slider);
      if (dots.length) {
        const idx = Math.round(track.scrollLeft / Math.max(step(), 1));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    $$('[data-v2-dot]', slider).forEach((d, i) => d.addEventListener('click', () => track.scrollTo({ left: i * step(), behavior: 'smooth' })));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  });
}

/* ---------- Produktkarten: Farbwechsel über Swatches ---------- */
function initCardSwatches(root = document) {
  $$('.v2-card', root).forEach((card) => {
    if (card.dataset.v2Bound) return;
    card.dataset.v2Bound = '1';
    const swatches = $$('.v2-swatch', card);
    if (!swatches.length) return;
    const img1 = $('.v2-card__img--1', card);
    const img2 = $('.v2-card__img--2', card);
    const links = $$('a[data-v2-card-link]', card);
    const price = $('.v2-card__price', card);
    const apply = (sw) => {
      swatches.forEach((s) => s.classList.toggle('is-active', s === sw));
      if (img1 && sw.dataset.img) { img1.src = sw.dataset.img; img1.removeAttribute('srcset'); }
      if (img2) {
        if (sw.dataset.img2) { img2.src = sw.dataset.img2; img2.removeAttribute('srcset'); img1?.classList.add('has-alt'); }
        else img1?.classList.remove('has-alt');
      }
      if (sw.dataset.url) links.forEach((a) => (a.href = sw.dataset.url));
      if (price && sw.dataset.price) price.innerHTML = sw.dataset.price;
    };
    swatches.forEach((sw) => {
      sw.addEventListener('click', (e) => { e.preventDefault(); apply(sw); });
      sw.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) apply(sw); });
    });
  });
}

/* ---------- Empfehlungen per Section Rendering nachladen ---------- */
function initRecommendations() {
  $$('[data-v2-recos]').forEach(async (el) => {
    if (el.dataset.v2Bound) return;
    el.dataset.v2Bound = '1';
    try {
      const res = await fetch(el.dataset.url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-v2-recos]');
      if (fresh && fresh.querySelector('.v2-card')) {
        el.innerHTML = fresh.innerHTML;
        el.closest('section')?.removeAttribute('hidden');
        initCardSwatches(el); initSliders(el.closest('[data-v2-slider]')?.parentElement || el); initReveal(el);
      } else if (el.dataset.hideEmpty !== undefined) {
        el.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    } catch (e) { console.warn('[v2] Empfehlungen', e); }
  });
}

/* ---------- Warenkorb: hinzufügen & Horizon-Drawer aktualisieren ---------- */
async function addToCart(items, sourceEl) {
  const sectionIds = $$('cart-items-component').map((c) => c.dataset.sectionId).filter(Boolean);
  const body = { items };
  if (sectionIds.length) { body.sections = sectionIds.join(','); body.sections_url = window.location.pathname; }
  const res = await fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || t('error', 'Fehler beim Hinzufügen'));
  const itemCount = items.reduce((n, i) => n + (Number(i.quantity) || 1), 0);
  const evt = new CartAddEvent({}, sourceEl?.id || 'v2', {
    source: 'product-form-component',
    itemCount,
    productId: String(items[0].product_id || ''),
    variantId: String(items[0].id),
    sections: data.sections,
  });
  (sourceEl || document).dispatchEvent(evt);
  // Fallback, falls kein Drawer existiert
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) window.location.href = '/cart';
  else setTimeout(() => { try { drawer.open(); } catch (e) {} }, 350);
  return data;
}

/* ---------- Produktseite ---------- */
class V2Product {
  constructor(root) {
    this.root = root;
    this.data = JSON.parse($('[data-v2-product-json]', root).textContent);
    this.product = this.data.product;
    this.form = $('form[data-v2-form]', root);
    this.idInput = $('input[name="id"]', this.form);
    this.button = $('[data-v2-atc]', root);
    this.stock = $('[data-v2-stock]', root);
    this.variant = this.product.variants.find((v) => v.id === Number(this.idInput.value)) || this.product.variants[0];
    this.sticky = $('[data-v2-sticky]', root.closest('.shopify-section') || document) || $('[data-v2-sticky]');
    this.initGallery();
    this.initOptions();
    this.initSubmit();
    this.initUpsell();
    this.initSticky();
    this.render(false);
  }

  /* Galerie */
  initGallery() {
    const g = $('[data-v2-gallery]', this.root);
    if (!g) return;
    this.track = $('[data-v2-gallery-track]', g);
    this.slides = $$('.v2-gallery__slide', g);
    this.thumbs = $$('.v2-gallery__thumb', g);
    this.counter = $('[data-v2-gallery-counter]', g);
    const go = (i) => {
      const s = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
      if (s) this.track.scrollTo({ left: s.offsetLeft - this.track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    };
    this.goTo = go;
    this.thumbs.forEach((th, i) => th.addEventListener('click', () => go(i)));
    $('[data-v2-gallery-prev]', g)?.addEventListener('click', () => go(this.index() - 1));
    $('[data-v2-gallery-next]', g)?.addEventListener('click', () => go(this.index() + 1));
    this.track.addEventListener('scroll', () => requestAnimationFrame(() => {
      const i = this.index();
      this.thumbs.forEach((th, k) => th.classList.toggle('is-active', k === i));
      const active = this.thumbs[i];
      if (active) active.parentElement.scrollTo({ top: active.offsetTop - active.parentElement.clientHeight / 2 + active.clientHeight / 2, behavior: 'smooth' });
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
      // Videos außerhalb der Ansicht pausieren
      this.slides.forEach((s, k) => { if (k !== i) $$('video', s).forEach((v) => v.pause()); });
    }), { passive: true });
    // Zoom
    const dialog = $('[data-v2-zoom-dialog]', this.root);
    if (dialog) {
      $$('[data-v2-zoom-open]', g).forEach((btn) => btn.addEventListener('click', () => {
        dialog.showModal();
        const i = this.index();
        const t2 = $('.v2-zoom-dialog__track', dialog);
        const slide = $$('.v2-zoom-dialog__slide', dialog)[i];
        if (t2 && slide) t2.scrollLeft = slide.offsetLeft;
      }));
      $('[data-v2-zoom-close]', dialog)?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target.tagName === 'IMG') dialog.close(); });
    }
  }
  index() {
    if (!this.track) return 0;
    const w = this.track.clientWidth || 1;
    return Math.round(this.track.scrollLeft / w);
  }

  /* Optionen */
  initOptions() {
    this.root.addEventListener('change', (e) => {
      if (!e.target.matches('[data-v2-option]')) return;
      this.selectFromOptions();
    });
    if (this.sticky) {
      $$('[data-v2-sticky-value]', this.sticky).forEach((btn) => btn.addEventListener('click', () => {
        const input = $$(`[data-v2-option][data-index="${btn.dataset.index}"]`, this.root).find((i) => i.value === btn.dataset.v2StickyValue);
        if (input) { input.checked = true; this.selectFromOptions(); }
      }));
    }
  }
  selectedOptions() {
    return this.product.options.map((_, i) => {
      const checked = $(`[data-v2-option][data-index="${i}"]:checked`, this.root) || $(`select[data-v2-option][data-index="${i}"]`, this.root);
      return checked ? checked.value : null;
    });
  }
  selectFromOptions() {
    const opts = this.selectedOptions();
    const v = this.product.variants.find((variant) => variant.options.every((o, i) => opts[i] === null || o === opts[i]));
    this.variant = v || null;
    this.render(true);
  }
  render(changed) {
    const v = this.variant;
    const btnText = $('[data-v2-atc-text]', this.button);
    const priceEls = $$('[data-v2-price]', this.root.closest('.shopify-section') || this.root);
    // Verfügbarkeit je Wert markieren
    this.product.options.forEach((_, idx) => {
      $$(`[data-v2-option][data-index="${idx}"]`, this.root).forEach((input) => {
        const opts = this.selectedOptions();
        opts[idx] = input.value;
        const match = this.product.variants.find((x) => x.options.every((o, i) => o === opts[i]));
        input.closest('.v2-size-card, .v2-pill')?.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
    if (!v) {
      this.button.disabled = true;
      if (btnText) btnText.textContent = t('unavailable', 'Nicht verfügbar');
      this.setStock('out', t('unavailable', 'Nicht verfügbar'));
      return;
    }
    this.idInput.value = v.id;
    const hasNote = !!(this.stock && this.stock.hasAttribute('data-v2-preorder'));
    const preorder = !v.available ? false : (hasNote || (v.inventory_management && v.inventory_policy === 'continue' && v.inventory_quantity <= 0));
    this.button.disabled = !v.available;
    if (btnText) btnText.textContent = !v.available ? t('soldout', 'Ausverkauft') : preorder ? t('preorder', 'Jetzt vorbestellen') : t('add', 'In den Warenkorb');
    const priceHtml = v.compare_at_price > v.price
      ? `<span class="is-sale">${money(v.price)}</span><s>${money(v.compare_at_price)}</s>`
      : `<span>${money(v.price)}</span>`;
    priceEls.forEach((el) => (el.innerHTML = priceHtml));
    if (!v.available) this.setStock('out', t('soldout_long', 'Derzeit ausverkauft'));
    else if (preorder) this.setStock('preorder', hasNote ? t('preorder_note', 'Vorbestellung · Versand wie oben angegeben') : t('preorder_long', 'Vorbestellbar · Versand sobald verfügbar'));
    else if (v.inventory_management && v.inventory_quantity > 0 && v.inventory_quantity <= 3) this.setStock('low', t('low', 'Nur noch wenige verfügbar · Lieferzeit 2–4 Werktage'));
    else this.setStock('in', t('instock', 'Auf Lager · Lieferzeit 2–4 Werktage'));
    $$('[data-v2-option-value]', this.root).forEach((el) => { el.textContent = v.options[Number(el.dataset.index)] || ''; });
    if (this.sticky) {
      $$('[data-v2-sticky-value]', this.sticky).forEach((b) => b.classList.toggle('is-active', v.options[Number(b.dataset.index)] === b.dataset.v2StickyValue));
      const sBtn = $('[data-v2-sticky-atc]', this.sticky);
      if (sBtn) { sBtn.disabled = !v.available; sBtn.querySelector('span').textContent = btnText ? btnText.textContent : ''; }
      const sv = $('[data-v2-sticky-variant]', this.sticky);
      if (sv) sv.textContent = [this.data.color, ...v.options.filter((o) => o !== 'Default Title')].filter(Boolean).join(' · ');
    }
    if (changed) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      if (v.featured_media && this.slides) {
        const i = this.slides.findIndex((s) => Number(s.dataset.mediaId) === v.featured_media.id);
        if (i > -1 && this.goTo) this.goTo(i);
      }
    }
  }
  setStock(state, text) {
    if (!this.stock) return;
    this.stock.className = `v2-stock v2-stock--${state}`;
    this.stock.textContent = text;
  }

  /* Absenden */
  initSubmit() {
    const err = $('[data-v2-error]', this.root);
    const submit = async (btn) => {
      if (!this.variant || !this.variant.available) return;
      btn.classList.add('is-loading');
      if (err) err.textContent = '';
      try {
        const props = {};
        new FormData(this.form).forEach((val, key) => { const m = key.match(/^properties\[(.+)\]$/); if (m && val) props[m[1]] = val; });
        await addToCart([{ id: this.variant.id, quantity: Number($('[name="quantity"]', this.form)?.value || 1), product_id: this.product.id, properties: props }], this.form);
      } catch (e) {
        if (err) err.textContent = e.message;
      } finally {
        btn.classList.remove('is-loading');
      }
    };
    this.form.addEventListener('submit', (e) => { e.preventDefault(); submit(this.button); });
    const sBtn = this.sticky && $('[data-v2-sticky-atc]', this.sticky);
    sBtn?.addEventListener('click', () => submit(sBtn));
  }

  initUpsell() {
    $$('[data-v2-upsell]', this.root).forEach((btn) => btn.addEventListener('click', async () => {
      btn.classList.add('is-loading');
      try {
        await addToCart([{ id: Number(btn.dataset.variantId), quantity: 1, product_id: btn.dataset.productId }], btn);
        btn.classList.add('is-added');
        btn.querySelector('span').textContent = t('added', 'Hinzugefügt');
      } catch (e) { console.warn(e); }
      btn.classList.remove('is-loading');
    }));
  }

  /* Sticky-Kaufleiste */
  initSticky() {
    if (!this.sticky) return;
    let pastButton = false;
    let footerVisible = false;
    const update = () => this.sticky.classList.toggle('is-visible', pastButton && !footerVisible);
    new IntersectionObserver(([e]) => {
      pastButton = !e.isIntersecting && e.boundingClientRect.top < 0;
      update();
    }).observe(this.button);
    const footer = $('.v2-footer') || $('footer');
    if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; update(); }).observe(footer);
  }
}

/* ---------- "Into the details" ---------- */
function initDetails(root = document) {
  $$('[data-v2-details]', root).forEach((sec) => {
    if (sec.dataset.v2Bound) return;
    sec.dataset.v2Bound = '1';
    const imgs = $$('.v2-details__img', sec);
    const thumbs = $$('.v2-details__thumbs button', sec);
    const descs = $$('[data-v2-desc]', sec);
    const items = $$('.v2-details__item', sec);
    const tabs = $$('.v2-details__tab', sec);
    const panels = $$('.v2-details__panel', sec);
    const show = (key) => {
      imgs.forEach((i) => i.classList.toggle('is-active', i.dataset.key === key));
      thumbs.forEach((i) => i.classList.toggle('is-active', i.dataset.key === key));
      items.forEach((i) => { const on = i.dataset.key === key; i.classList.toggle('is-active', on); i.setAttribute('aria-selected', on); });
      descs.forEach((d) => (d.hidden = d.dataset.key !== key));
    };
    items.forEach((i) => i.addEventListener('click', () => show(i.dataset.key)));
    thumbs.forEach((i) => i.addEventListener('click', () => {
      const item = items.find((x) => x.dataset.key === i.dataset.key);
      const panel = item?.closest('.v2-details__panel');
      if (panel) tabs.forEach((tb) => { if (tb.dataset.panel === panel.id) tb.click(); });
      show(i.dataset.key);
    }));
    tabs.forEach((tab) => tab.addEventListener('click', () => {
      tabs.forEach((x) => { x.classList.toggle('is-active', x === tab); x.setAttribute('aria-selected', x === tab); });
      panels.forEach((p) => (p.hidden = p.id !== tab.dataset.panel));
      const first = $('.v2-details__item', $(`#${tab.dataset.panel}`, sec));
      if (first) show(first.dataset.key);
    }));
  });
}

/* ---------- Film-Dialog ---------- */
function initFilm(root = document) {
  $$('[data-v2-film]', root).forEach((sec) => {
    if (sec.dataset.v2Bound) return;
    sec.dataset.v2Bound = '1';
    const dialog = $('dialog', sec);
    const frame = $('.v2-video-dialog__frame', dialog);
    const open = () => {
      const src = sec.dataset.embed;
      if (!src) return;
      if (sec.dataset.type === 'file') frame.innerHTML = `<video src="${src}" controls autoplay playsinline></video>`;
      else frame.innerHTML = `<iframe src="${src}" title="Video" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>`;
      dialog.showModal();
    };
    $$('[data-v2-film-open]', sec).forEach((b) => b.addEventListener('click', open));
    const close = () => dialog.close();
    $('[data-v2-film-close]', dialog)?.addEventListener('click', close);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
    dialog.addEventListener('close', () => { frame.innerHTML = ''; });
  });
}

/* ---------- Vergleich ---------- */
function initCompare(root = document) {
  $$('[data-v2-compare]', root).forEach((sec) => {
    if (sec.dataset.v2Bound) return;
    sec.dataset.v2Bound = '1';
    const data = JSON.parse($('[data-v2-compare-json]', sec).textContent);
    $$('select[data-v2-compare-select]', sec).forEach((sel) => sel.addEventListener('change', () => {
      const col = sel.closest('.v2-compare__col');
      const item = data[Number(sel.value)];
      if (!item) return;
      col.classList.remove('is-swapping'); void col.offsetWidth; col.classList.add('is-swapping');
      const img = $('img', col);
      if (img) { img.src = item.image; img.removeAttribute('srcset'); img.alt = item.title; }
      $('[data-v2-compare-price]', col).innerHTML = item.price;
      $$('[data-v2-compare-value]', col).forEach((dd, i) => (dd.innerHTML = item.values[i] || '–'));
      const cta = $('[data-v2-compare-cta]', col);
      if (cta) { cta.href = item.url; cta.hidden = !item.url; }
    }));
  });
}

/* ---------- Kollektion: Sortierung ---------- */
function initSort() {
  $$('[data-v2-sort]').forEach((sel) => sel.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }));
}

/* ---------- Start ---------- */
function initAll(root = document) {
  initReveal(root);
  initAccordions(root);
  initSliders(root);
  initCardSwatches(root);
  initDetails(root);
  initFilm(root);
  initCompare(root);
  $$('[data-v2-product]', root).forEach((el) => { if (!el.v2) el.v2 = new V2Product(el); });
}

function boot() {
  window.v2Booted = true;
  trackHeaderHeight();
  const go = () => { initAll(); initRecommendations(); initSort(); };
  if (document.fonts && document.fonts.ready) {
    // Für exakte Zeilenumbrüche auf Schriften warten (max. 1,2 s)
    Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 1200))]).then(go);
  } else go();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

// Theme-Editor: Sections nach Bearbeitung neu initialisieren
document.addEventListener('shopify:section:load', (e) => { initAll(e.target); initRecommendations(); });
